import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import Icon from '../../../../ui/global/Icon';
import InlineInput from '../../../../ui/components/InlineInput';

const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 14px;
    line-height: 1.2em;
    background-color: ${Colors.transparent};
    border: 1px solid ${Colors.transparent};
    border-radius: 12px;
    padding: 0 2px;
    margin: auto 0;

    &.has-content {
        background-color: ${Colors.black+60};
    }
    
    &:hover, &.focused {
        border: 1px solid ${Colors.grey7};
        background-color: ${Colors.black+60};
    }
`;

const SearchIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 10px;
    cursor: pointer;
`;

const SearchInputContainer = styled.div<{ $isVisible: boolean }>`
    width: ${props => props.$isVisible ? 'auto' : '0'};
    overflow: hidden;
    transition: width 0.3s ease;
`;

interface SearchProps {
    searchTerm: string;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch: () => void;
    minWidth?: string;
}

const Search: React.FC<SearchProps> = ({ 
    searchTerm, 
    onSearchChange, 
    onSearch,
    minWidth = "10vw"
}) => {
    const [isSearchFocused, setIsSearchFocused] = React.useState(false);
    const [isSearchHovered, setIsSearchHovered] = React.useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    
    useEffect(() => {
        const inputElement = searchInputRef.current;
        
        if (!inputElement) return;
        
        const handleFocus = () => setIsSearchFocused(true);
        const handleBlur = () => setIsSearchFocused(false);
        
        inputElement.addEventListener('focus', handleFocus);
        inputElement.addEventListener('blur', handleBlur);
        
        return () => {
            inputElement.removeEventListener('focus', handleFocus);
            inputElement.removeEventListener('blur', handleBlur);
        };
    }, []);
    
    const handleSearchContainerMouseEnter = () => {
        setIsSearchHovered(true);
    };

    const handleSearchContainerMouseLeave = () => {
        setIsSearchHovered(false);
    };
    
    const handleSearchKeyPress = () => {
        onSearch();
    };
    
    const handleSearchIconClick = () => {
        onSearch();
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    return (
        <SearchContainer 
            onMouseEnter={handleSearchContainerMouseEnter}
            onMouseLeave={handleSearchContainerMouseLeave}
            className={`${isSearchFocused ? 'focused' : ''} ${searchTerm.length > 0 ? 'has-content' : ''}`}
        >
            <SearchInputContainer $isVisible={isSearchHovered || isSearchFocused || searchTerm.length > 0}>
                <InlineInput
                    value={searchTerm}
                    onChange={onSearchChange}
                    placeholder="Search..."
                    colorMode="dark"
                    minWidth={minWidth}
                    onEnterPress={handleSearchKeyPress}
                    ref={searchInputRef}
                />
            </SearchInputContainer>
            <SearchIconContainer>
                <Icon
                    icon="search"
                    color="white"
                    size={16}
                    onClick={handleSearchIconClick}
                />
            </SearchIconContainer>
        </SearchContainer>
    );
};

export default Search;